import { useState, useEffect } from "react";
import { useAuth, getAuthHeaders } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Heart, MessageCircle, Send, LogOut, User, Home, BookOpen, Pencil, X, Check, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalPage() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [members, setMembers] = useState([]);
  const [newPost, setNewPost] = useState("");
  const [profile, setProfile] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [activeTab, setActiveTab] = useState("feed");

  useEffect(() => {
    if (!loading && user === false) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      fetchPosts();
      fetchProfile();
      fetchMembers();
    }
  }, [user]);

  const fetchPosts = async () => {
    try {
      const { data } = await axios.get(`${API}/portal/posts`, { headers: getAuthHeaders() });
      setPosts(data);
    } catch (e) { console.error(e); }
  };

  const fetchProfile = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { headers: getAuthHeaders() });
      setProfile(data);
      setProfileForm(data);
    } catch (e) { console.error(e); }
  };

  const fetchMembers = async () => {
    try {
      const { data } = await axios.get(`${API}/portal/members`, { headers: getAuthHeaders() });
      setMembers(data);
    } catch (e) { console.error(e); }
  };

  const handlePost = async () => {
    if (!newPost.trim()) return;
    try {
      await axios.post(`${API}/portal/posts`, { content: newPost }, { headers: getAuthHeaders() });
      setNewPost("");
      fetchPosts();
      toast.success("Post shared!");
    } catch (e) { toast.error("Failed to post"); }
  };

  const handleLike = async (postId) => {
    try {
      await axios.post(`${API}/portal/posts/${postId}/like`, {}, { headers: getAuthHeaders() });
      fetchPosts();
    } catch (e) { console.error(e); }
  };

  const handleComment = async (postId) => {
    const content = commentInputs[postId];
    if (!content?.trim()) return;
    try {
      await axios.post(`${API}/portal/posts/${postId}/comment`, { content }, { headers: getAuthHeaders() });
      setCommentInputs({ ...commentInputs, [postId]: "" });
      fetchPosts();
    } catch (e) { console.error(e); }
  };

  const handleProfileSave = async () => {
    try {
      await axios.put(`${API}/portal/profile`, {
        name: profileForm.name,
        birthday: profileForm.birthday,
        favorite_scriptures: profileForm.favorite_scriptures,
        prayer_needs: profileForm.prayer_needs,
        hobbies: profileForm.hobbies,
        bio: profileForm.bio,
      }, { headers: getAuthHeaders() });
      setEditingProfile(false);
      fetchProfile();
      toast.success("Profile updated!");
    } catch (e) { toast.error("Failed to update profile"); }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getInitials = (name) => {
    return name ? name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) : "?";
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p className="font-['Satoshi'] text-zinc-500">Loading...</p></div>;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="portal-page">
      {/* Portal Nav */}
      <nav className="bg-white border-b border-zinc-200 sticky top-0 z-50" data-testid="portal-nav">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/" className="font-['Clash_Display'] font-bold text-xl">
            LIGHT CITY <span className="text-zinc-400">PORTAL</span>
          </a>
          <div className="flex items-center gap-4">
            <span className="font-['Satoshi'] text-sm text-zinc-600 hidden sm:block">{profile?.name || user.name}</span>
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs"
              style={{ backgroundColor: profile?.avatar_color || "#3b82f6" }}
              data-testid="portal-avatar"
            >
              {getInitials(profile?.name || user.name)}
            </div>
            <button onClick={handleLogout} className="text-zinc-500 hover:text-black transition-colors" data-testid="portal-logout">
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-8 grid grid-cols-1 md:grid-cols-12 gap-8">
        {/* Left Sidebar - Profile */}
        <aside className="md:col-span-3 space-y-4" data-testid="portal-sidebar">
          <div className="bg-white p-6 border border-zinc-200">
            <div className="text-center mb-4">
              <div
                className="w-20 h-20 rounded-full mx-auto mb-3 flex items-center justify-center text-white font-bold text-2xl"
                style={{ backgroundColor: profile?.avatar_color || "#3b82f6" }}
              >
                {getInitials(profile?.name || user.name)}
              </div>
              <h2 className="font-['Clash_Display'] font-bold text-lg">{profile?.name || user.name}</h2>
              <p className="font-['Satoshi'] text-sm text-zinc-500">Light City Community</p>
            </div>
            <div className="space-y-2 mt-4">
              <button onClick={() => setActiveTab("feed")} className={`flex items-center gap-3 w-full p-2 text-left font-['Satoshi'] text-sm transition-colors ${activeTab === "feed" ? "text-black font-semibold" : "text-zinc-500 hover:text-black"}`} data-testid="tab-feed">
                <Home size={18} /> Feed
              </button>
              <button onClick={() => setActiveTab("profile")} className={`flex items-center gap-3 w-full p-2 text-left font-['Satoshi'] text-sm transition-colors ${activeTab === "profile" ? "text-black font-semibold" : "text-zinc-500 hover:text-black"}`} data-testid="tab-profile">
                <User size={18} /> My Account
              </button>
              <a href="/messages" className="flex items-center gap-3 w-full p-2 text-left font-['Satoshi'] text-sm text-zinc-500 hover:text-black transition-colors" data-testid="tab-messages">
                <Mail size={18} /> Messages
              </a>
              <a href="/" className="flex items-center gap-3 w-full p-2 text-left font-['Satoshi'] text-sm text-zinc-500 hover:text-black transition-colors">
                <BookOpen size={18} /> Back to Website
              </a>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="md:col-span-6 space-y-4" data-testid="portal-main">
          {activeTab === "feed" && (
            <>
              {/* Post Composer */}
              <div className="bg-white p-4 border border-zinc-200" data-testid="post-composer">
                <div className="flex gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-xs"
                    style={{ backgroundColor: profile?.avatar_color || "#3b82f6" }}
                  >
                    {getInitials(profile?.name || user.name)}
                  </div>
                  <Textarea
                    value={newPost}
                    onChange={(e) => setNewPost(e.target.value)}
                    placeholder="What's happening in the City?"
                    className="input-sharp border-none shadow-none focus-visible:ring-0 resize-none min-h-[60px]"
                    data-testid="post-input"
                  />
                </div>
                <div className="flex justify-end mt-3 border-t border-zinc-100 pt-3">
                  <Button
                    onClick={handlePost}
                    disabled={!newPost.trim()}
                    className="btn-sharp bg-black text-white hover:bg-zinc-800 px-6 py-2 text-sm rounded-full"
                    data-testid="post-submit"
                  >
                    Post
                  </Button>
                </div>
              </div>

              {/* Posts Feed */}
              <div className="bg-white border border-zinc-200 overflow-hidden" data-testid="posts-feed">
                {posts.map((post) => (
                  <div key={post.id} className="p-4 border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`post-${post.id}`}>
                    <div className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                          style={{ backgroundColor: post.author_color || "#3b82f6" }}
                        >
                          {getInitials(post.author_name)}
                        </div>
                        {post.comments?.length > 0 && <div className="w-0.5 flex-1 bg-zinc-200 mt-2" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <span className="font-['Clash_Display'] font-semibold">{post.author_name}</span>
                          <span className="font-['Satoshi'] text-zinc-400 text-sm">{timeAgo(post.created_at)}</span>
                        </div>
                        <p className="font-['Satoshi'] text-zinc-800 mt-1 leading-relaxed">{post.content}</p>
                        <div className="flex gap-6 mt-3 text-zinc-500 text-sm">
                          <button onClick={() => handleLike(post.id)} className="flex items-center gap-1 hover:text-red-500 transition-colors" data-testid={`like-btn-${post.id}`}>
                            <Heart size={16} fill={post.likes?.includes(profile?.id) ? "currentColor" : "none"} /> {post.likes?.length || 0}
                          </button>
                          <button className="flex items-center gap-1 hover:text-blue-500 transition-colors">
                            <MessageCircle size={16} /> {post.comments?.length || 0}
                          </button>
                        </div>

                        {/* Comments */}
                        {post.comments?.map((comment) => (
                          <div key={comment.id} className="flex gap-2 mt-3 pl-2 border-l-2 border-zinc-100">
                            <div
                              className="w-6 h-6 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
                              style={{ backgroundColor: comment.author_color || "#3b82f6", fontSize: "8px" }}
                            >
                              {getInitials(comment.author_name)}
                            </div>
                            <div>
                              <span className="font-['Satoshi'] font-semibold text-xs">{comment.author_name}</span>
                              <p className="font-['Satoshi'] text-zinc-600 text-sm">{comment.content}</p>
                            </div>
                          </div>
                        ))}

                        {/* Comment Input */}
                        <div className="flex gap-2 mt-3">
                          <Input
                            value={commentInputs[post.id] || ""}
                            onChange={(e) => setCommentInputs({ ...commentInputs, [post.id]: e.target.value })}
                            placeholder="Write a comment..."
                            className="input-sharp h-8 text-sm"
                            onKeyDown={(e) => e.key === "Enter" && handleComment(post.id)}
                            data-testid={`comment-input-${post.id}`}
                          />
                          <button onClick={() => handleComment(post.id)} className="text-zinc-500 hover:text-black" data-testid={`comment-submit-${post.id}`}>
                            <Send size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {posts.length === 0 && (
                  <div className="p-8 text-center font-['Satoshi'] text-zinc-400">No posts yet. Be the first!</div>
                )}
              </div>
            </>
          )}

          {activeTab === "profile" && profile && (
            <div className="bg-white p-6 md:p-8 border border-zinc-200" data-testid="profile-section">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-['Clash_Display'] font-semibold text-2xl">My Account</h2>
                {!editingProfile ? (
                  <Button onClick={() => setEditingProfile(true)} variant="outline" className="btn-sharp text-sm flex items-center gap-2" data-testid="edit-profile-btn">
                    <Pencil size={16} /> Edit Profile
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button onClick={handleProfileSave} className="btn-sharp bg-black text-white text-sm flex items-center gap-2" data-testid="save-profile-btn">
                      <Check size={16} /> Save
                    </Button>
                    <Button onClick={() => { setEditingProfile(false); setProfileForm(profile); }} variant="outline" className="btn-sharp text-sm flex items-center gap-2" data-testid="cancel-edit-btn">
                      <X size={16} /> Cancel
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <div>
                  <label className="font-['Satoshi'] font-medium text-sm mb-2 block text-zinc-500">Full Name</label>
                  {editingProfile ? (
                    <Input value={profileForm.name || ""} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} className="input-sharp h-12" data-testid="profile-name" />
                  ) : (
                    <p className="font-['Satoshi'] text-lg">{profile.name}</p>
                  )}
                </div>

                <div>
                  <label className="font-['Satoshi'] font-medium text-sm mb-2 block text-zinc-500">Email</label>
                  <p className="font-['Satoshi'] text-lg text-zinc-600">{profile.email}</p>
                </div>

                <div>
                  <label className="font-['Satoshi'] font-medium text-sm mb-2 block text-zinc-500">Birthday</label>
                  {editingProfile ? (
                    <Input type="date" value={profileForm.birthday || ""} onChange={(e) => setProfileForm({ ...profileForm, birthday: e.target.value })} className="input-sharp h-12" data-testid="profile-birthday" />
                  ) : (
                    <p className="font-['Satoshi'] text-lg">{profile.birthday || "Not set"}</p>
                  )}
                </div>

                <div>
                  <label className="font-['Satoshi'] font-medium text-sm mb-2 block text-zinc-500">Favorite Scriptures</label>
                  {editingProfile ? (
                    <Textarea value={profileForm.favorite_scriptures || ""} onChange={(e) => setProfileForm({ ...profileForm, favorite_scriptures: e.target.value })} className="input-sharp" placeholder="e.g. Psalm 23, John 3:16" data-testid="profile-scriptures" />
                  ) : (
                    <p className="font-['Satoshi'] text-lg">{profile.favorite_scriptures || "Not set"}</p>
                  )}
                </div>

                <div>
                  <label className="font-['Satoshi'] font-medium text-sm mb-2 block text-zinc-500">Prayer Needs</label>
                  {editingProfile ? (
                    <Textarea value={profileForm.prayer_needs || ""} onChange={(e) => setProfileForm({ ...profileForm, prayer_needs: e.target.value })} className="input-sharp" placeholder="Share your prayer requests..." data-testid="profile-prayer" />
                  ) : (
                    <p className="font-['Satoshi'] text-lg">{profile.prayer_needs || "Not set"}</p>
                  )}
                </div>

                <div>
                  <label className="font-['Satoshi'] font-medium text-sm mb-2 block text-zinc-500">Hobbies</label>
                  {editingProfile ? (
                    <Textarea value={profileForm.hobbies || ""} onChange={(e) => setProfileForm({ ...profileForm, hobbies: e.target.value })} className="input-sharp" placeholder="e.g. Reading, music, volunteering" data-testid="profile-hobbies" />
                  ) : (
                    <p className="font-['Satoshi'] text-lg">{profile.hobbies || "Not set"}</p>
                  )}
                </div>

                <div>
                  <label className="font-['Satoshi'] font-medium text-sm mb-2 block text-zinc-500">Bio</label>
                  {editingProfile ? (
                    <Textarea value={profileForm.bio || ""} onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })} className="input-sharp" placeholder="Tell us about yourself..." data-testid="profile-bio" />
                  ) : (
                    <p className="font-['Satoshi'] text-lg">{profile.bio || "Not set"}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </main>

        {/* Right Sidebar - New Members */}
        <aside className="md:col-span-3 space-y-4" data-testid="portal-right-sidebar">
          <div className="bg-white p-6 border border-zinc-200">
            <h3 className="font-['Clash_Display'] font-bold mb-4">Community Members</h3>
            <div className="space-y-4">
              {members.slice(0, 8).map((member, i) => (
                <div key={i} className="flex items-center justify-between" data-testid={`member-${i}`}>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: member.avatar_color || "#3b82f6", fontSize: "10px" }}
                    >
                      {getInitials(member.name)}
                    </div>
                    <span className="font-['Satoshi'] text-sm font-medium">{member.name}</span>
                  </div>
                </div>
              ))}
              {members.length === 0 && (
                <p className="font-['Satoshi'] text-sm text-zinc-400">No members yet</p>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
